import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["contentName", "professorId", "useCase"];
import enviro from 'enviro';
import findIndex from 'hs-lodash/findIndex';
import maxBy from 'hs-lodash/maxBy';
import { useCallback, useEffect, useMemo } from 'react';
import { Redirect, useHistory, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { NavMarker } from 'react-rhumb';
import getLang from 'LearningCenterUI/utils/getLang';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { Flex } from 'foundations-components/components/Flex';
import { View } from 'foundations-components/components/View';
import { LoadingSpinner } from 'foundations-components/transitional/components/LoadingSpinner';
import { HR } from 'foundations-components/transitional/components/HR';
import ACNavigatorButton from 'academy-common-lib/components/navigation/contentNavigator/ACNavigatorButton';
import { useMediaQuery } from 'academy-common-lib/utils/Styling';
import ApiError from '../components/Errors/ApiError';
import LearningPageHeader from '../containers/TrackPage/LearningPageHeader';
import LearningPageSidebar from '../containers/TrackPage/LearningPageSidebar';
import LearningPageMainSection from '../containers/TrackPage/LearningPageMainSection';
import LearningContentInnerWrapper from '../features/learningContent/components/LearningContentInnerWrapper';
import useWootricSurvey from '../hooks/useWootricSurvey';
import UsageTracker from '../utils/UsageTracker';
import LessonPage from '../components/LessonPage/LessonPage';

// @ts-expect-error Could not find a declaration file for module
import { useSearchParam } from '../hooks/searchParamHooks';
import { SCREENS } from '../constants/Tracking';
import { fetchLessonIfNeeded
// @ts-expect-error Could not find a declaration file for module
} from '../actions/LessonActions';
import { getContentTypeForTracking } from '../utils/ContentUtils';
// @ts-expect-error Could not find a declaration file for module
import { getLesson } from '../selectors/lessonSelectors';
import { FILTER_TYPES } from '../constants/TopFilterConstants';
import { useFullLesson } from '../features/lessons';
import { useLessonProgressById, usePlaylistProgress } from '../features/userProgress';
import { TRACK_NEXT_IN_PLAYLIST_URL_FRAGMENT, TRACK_PLAYLIST_COMPLETE_URL_FRAGMENT, TRACK_PLAYLIST_INCOMPLETE_URL_FRAGMENT, TRACK_RECOMMENDATIONS_URL_FRAGMENT, SOFTWARE_DISCOVERY_TOOL_RECOMMENDATIONS } from '../constants/Navigation';
import { HasParentPlaylist, NextPlaylistContent, usePlaylistIdFromSearch } from '../features/playlistContent';
import { useAvailableLanguageOrFallback } from '../features/contentLanguage';
import LessonNavigator from '../components/Navigator/LessonNavigator';
import { useSetLastClickedRecommendationItemFromUrl } from '../features/recommendations/recommendationsHooks';
import { useSingleLanguageRedirect } from '../hooks/useSingleLangaugeRedirect';
import { useGetAiAccessControls } from '../features/aiControl/aiAccessQueries';
import { useVideoPlayerCaptions } from '../features/video/videoHooks';
import { isInternalPortal } from '../features/accounts/components/ExternalPortalOnly';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const useCurrentLanguage = lesson => {
  const [[paramLanguage]] = useSearchParam(FILTER_TYPES.LANGUAGE);
  const selectedLessonLanguage = lesson ? lesson.get('language') || getLang() : getLang();
  return paramLanguage || selectedLessonLanguage;
};
const noRedirectRoutes = [TRACK_NEXT_IN_PLAYLIST_URL_FRAGMENT, TRACK_PLAYLIST_COMPLETE_URL_FRAGMENT, TRACK_PLAYLIST_INCOMPLETE_URL_FRAGMENT, TRACK_RECOMMENDATIONS_URL_FRAGMENT, SOFTWARE_DISCOVERY_TOOL_RECOMMENDATIONS];
export const useLessonRedirectUrl = (lessonId, language, contentId) => {
  const playlistId = usePlaylistIdFromSearch();
  const {
    playlistProgress
  } = usePlaylistProgress(playlistId, !playlistId);
  const {
    lessonProgressById,
    lessonProgressByIdError,
    lessonProgressByIdLoading
  } = useLessonProgressById(lessonId);
  const {
    lesson,
    lessonError,
    lessonLoading
  } = useFullLesson(lessonId, language);
  const {
    location
  } = useHistory();
  const lessonRedirectUrl = useMemo(() => {
    if (!lessonProgressById || !lesson) return null;

    // We never want to redirect from the pages shown at the end of a lesson
    for (const route of noRedirectRoutes) {
      if (location.pathname.includes(route)) return null;
    }
    if (contentId) {
      if (!lesson.content.some(content => content.id === contentId)) return `/lessons/${lessonId}`;else return null;
    }
    const allContentProgress = [];
    lesson.content.forEach(content => {
      const contentProgress = lessonProgressById.contentProgress.find(c => c.contentId === content.id);
      allContentProgress.push({
        contentId: content.id,
        status: contentProgress ? contentProgress.progressType : 'NOT_STARTED',
        updatedAt: contentProgress ? contentProgress.updatedAt : null,
        url: `/lessons/${lessonId}/${content.id}`
      });
    });
    let finalUrl;
    if (playlistId) {
      if (playlistProgress && playlistProgress.status !== 'COMPLETED') {
        finalUrl = `/lessons/${lessonId}/${TRACK_PLAYLIST_INCOMPLETE_URL_FRAGMENT}`;
      } else {
        finalUrl = allContentProgress[0].url;
      }
    } else {
      finalUrl = `/lessons/${lessonId}/${TRACK_RECOMMENDATIONS_URL_FRAGMENT}`;
    }
    allContentProgress.push({
      contentId: null,
      status: null,
      updatedAt: null,
      url: finalUrl
    });
    if (lessonProgressById.status === 'COMPLETED') {
      return allContentProgress[allContentProgress.length - 1].url;
    }
    const lastCompleted = maxBy(allContentProgress.filter(p => p.status === 'COMPLETED'), p => p.updatedAt);
    if (lastCompleted) {
      const lastCompletedIndex = findIndex(allContentProgress, lastCompleted);
      const unprogressedItemsAfterLastCompletedItem = allContentProgress.slice(lastCompletedIndex + 1).filter(p => p.status !== 'COMPLETED');
      return unprogressedItemsAfterLastCompletedItem[0].url;
    }
    return allContentProgress[0].url;
  }, [contentId, lesson, lessonId, lessonProgressById, location.pathname, playlistId, playlistProgress]);
  return {
    lessonRedirectUrl,
    lessonRedirectUrlLoading: lessonProgressByIdLoading || lessonLoading,
    lessonRedirectUrlError: lessonProgressByIdError || lessonError
  };
};
const getNextContentId = (lesson, contentId) => {
  if (!lesson || !lesson.get('contentIds') || !contentId) return null;
  const contentIds = lesson.get('contentIds');
  const currentIndex = contentIds.indexOf(contentId);
  return contentIds.get(currentIndex + 1, null);
};
const Lesson = () => {
  const {
    isMobile,
    isNotLaptop
  } = useMediaQuery();
  useWootricSurvey('CSAT');
  const dispatch = useDispatch();
  const {
    push,
    location
  } = useHistory();
  const contentById = useSelector(state => state.contentById);
  const {
    contentId,
    lessonId
  } = useParams();
  const content = contentId ? contentById.get(contentId) : null;
  const contentName = content && content.get('name');
  const contentType = getContentTypeForTracking(content);

  /**
   * Future TODO:
   * - Should pass the destructed object to children instead of `lesson`
   * - This will allow us to type lesson correctly since it will no longer be an Immutable
   */
  const lesson = useSelector(state => getLesson(state, {
    lessonId
  }));
  const language = useCurrentLanguage(lesson);
  const {
    lessonRedirectUrl
  } = useLessonRedirectUrl(Number(lessonId), language, Number(contentId));
  const {
    redirectUrl: singleLanguageRedirectUrl,
    loading: singleLanguageRedirectLoading
  } = useSingleLanguageRedirect({
    contentId: Number(lessonId),
    language,
    contentType: 'LESSON'
  });
  useSetLastClickedRecommendationItemFromUrl({
    id: lessonId ? Number(lessonId) : undefined,
    type: 'LESSON'
  });
  const nextContentId = getNextContentId(lesson, contentId);
  const handleAdvanceContent = useCallback(() => {
    if (nextContentId) {
      push(Object.assign({}, location, {
        pathname: `/lessons/${lesson.get('id')}/${nextContentId}`
      }));
    } else {
      push(Object.assign({}, location, {
        pathname: `recommendations`
      }));
    }
  }, [push, location, lesson, nextContentId]);
  const trackResourceClick = useCallback(({
    title,
    url
  }) => {
    UsageTracker.track('contentInteraction', {
      action: 'open-resource-link',
      contentId,
      contentLanguage: language,
      contentType,
      lessonId: lesson.get('id'),
      lessonName: lesson.get('name'),
      resourceTitle: title,
      resourceUrl: url
    });
  }, [language, lesson, contentId, contentType]);
  const {
    languageToLoad,
    availableLanguagesLoading
  } = useAvailableLanguageOrFallback({
    lessonId: Number(lessonId),
    language
  });
  const {
    lesson: fullLesson,
    lessonLoading: fullLessonLoading
  } = useFullLesson(Number(lessonId), language);
  const {
    lessonProgressById
  } = useLessonProgressById(Number(lessonId));
  const {
    currentTrackingProps,
    currentVideoId,
    currentVideoHublet,
    currentVideoCreatorPortalId,
    currentVideoFormattedCaptions
  } = useMemo(() => {
    if (!fullLesson) {
      return {
        currentFullContent: undefined,
        currentTrackingProps: undefined,
        currentVideoId: undefined,
        currentVideoHublet: undefined,
        currentVideoCreatorPortalId: undefined,
        currentVideoFormattedCaptions: undefined
      };
    }
    const _currentFullContent = fullLesson.content.find(fullContent => fullContent.id === Number(contentId));
    if (_currentFullContent) {
      var _currentFullContent$p;
      const trackingProps = {
        contentName: _currentFullContent.name,
        professorId: ((_currentFullContent$p = _currentFullContent.professors) === null || _currentFullContent$p === void 0 ? void 0 : _currentFullContent$p.map(professor => professor.id)) || []
      };
      return {
        currentFullContent: _currentFullContent,
        currentTrackingProps: trackingProps,
        currentVideoId: Number(_currentFullContent.reference),
        currentVideoHublet: _currentFullContent.hublet,
        currentVideoCreatorPortalId: _currentFullContent.hubId,
        currentVideoFormattedCaptions: !!_currentFullContent.formattedCaption || !!_currentFullContent.plainCaption
      };
    }
    return {
      currentFullContent: undefined,
      currentTrackingProps: undefined,
      currentVideoId: undefined,
      currentVideoHublet: undefined,
      currentVideoCreatorPortalId: undefined,
      currentVideoFormattedCaptions: undefined
    };
  }, [fullLesson, contentId]);
  const {
    instructorIds
  } = useMemo(() => {
    if (!fullLesson) {
      return {
        instructors: [],
        instructorIds: []
      };
    }
    const instructorsById = {};
    fullLesson.content.forEach(asset => {
      if (asset.professors) {
        asset.professors.forEach(instructor => {
          instructorsById[instructor.id] = instructor;
        });
      }
    });
    return {
      instructors: Object.values(instructorsById),
      instructorIds: Object.keys(instructorsById).map(Number)
    };
  }, [fullLesson]);
  const {
    aiAccessControls,
    loading: aiAccessControlsLoading
  } = useGetAiAccessControls();
  const aiEnabled = !!(aiAccessControls !== null && aiAccessControls !== void 0 && aiAccessControls.generativeAiEnabled);
  const {
    captionsAvailable: videoCaptionsAvailable,
    captionsLoading: videoCaptionsLoading
  } = useVideoPlayerCaptions(currentVideoId, currentVideoHublet, currentVideoCreatorPortalId);
  const captionsAvailable = videoCaptionsAvailable || currentVideoFormattedCaptions;

  // TODO: InteractiveEducation Tracking Disabled Until Release
  const interactiveEducationEnabled = isNotLaptop && captionsAvailable && aiEnabled;
  const lessonTrackingData = useMemo(() => fullLesson && {
    contentLanguage: fullLesson.language,
    contentName: fullLesson.name,
    creatorHubId: fullLesson.hubId,
    hsSoftwareFilters: fullLesson.hsSoftwareFilters,
    professorId: instructorIds,
    subCategory: fullLesson.subCategories,
    useCase: fullLesson.useCases
  }, [fullLesson, instructorIds]);
  const enableAdaptToIndustry = enviro.isQa() || isInternalPortal();
  useEffect(() => {
    if (!availableLanguagesLoading) dispatch(fetchLessonIfNeeded(lessonId, languageToLoad));
  }, [lessonId, languageToLoad, dispatch, availableLanguagesLoading]);
  useEffect(() => {
    if (fullLesson && lessonTrackingData && !aiAccessControlsLoading && !videoCaptionsLoading) {
      const restLessonTrackingData = _objectWithoutPropertiesLoose(lessonTrackingData, _excluded);
      const feature = interactiveEducationEnabled ? enableAdaptToIndustry ? ['AI-QUIZ-ME', 'AI-SUMMARIZATION', 'AI-ADAPT-TO-INDUSTRY'] : ['AI-QUIZ-ME', 'AI-SUMMARIZATION'] : [];
      UsageTracker.trackStandalone('Pageview', Object.assign({
        screen: SCREENS.LESSON,
        contentId,
        contentType,
        feature,
        lessonId,
        lessonName: fullLesson.name,
        allCategories: fullLesson.subCategories,
        useCases: fullLesson.useCases,
        businessObjectives: fullLesson.businessObjectives
      }, restLessonTrackingData, currentTrackingProps));
    }
  }, [contentId, contentType, lessonId, lessonTrackingData, currentTrackingProps, fullLesson, aiAccessControlsLoading, interactiveEducationEnabled, videoCaptionsLoading, enableAdaptToIndustry]);
  const assetProgress = useMemo(() => {
    if (!lessonProgressById || !lessonProgressById.contentProgress) {
      return {};
    }
    const progressMap = {};
    lessonProgressById.contentProgress.forEach(contentProgress => {
      progressMap[contentProgress.contentId] = contentProgress.progressType;
    });
    return progressMap;
  }, [lessonProgressById]);
  if (!singleLanguageRedirectLoading && lesson && lesson.get('hasError')) {
    return /*#__PURE__*/_jsx(ApiError, {
      contentType: "LESSON",
      error: lesson.get('recentError'),
      marker403: "LESSON_VIEW_API_403",
      marker404: "LESSON_VIEW_API_404",
      markerDefault: "LESSON_VIEW_API_ERROR"
    });
  }
  if (singleLanguageRedirectUrl) {
    return /*#__PURE__*/_jsx(Redirect, {
      to: {
        pathname: singleLanguageRedirectUrl,
        search: window.location.search
      }
    });
  }
  const isLoading = !lesson || availableLanguagesLoading || fullLessonLoading || !fullLesson || lesson.get('language', '').toUpperCase() !== language && lesson.get('isRequesting');
  const navigator = fullLesson && /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(LessonNavigator, {
      active: true,
      activeAssetId: Number(contentId),
      assets: fullLesson.content.map(asset => ({
        id: asset.id,
        name: asset.name,
        duration: asset.duration || 0,
        type: asset.contentType,
        linkType: asset.linkType,
        totalQuestions: asset.length || 0,
        completed: assetProgress[asset.id] === 'COMPLETED'
      })),
      duration: fullLesson.duration,
      id: Number(lessonId),
      language: language,
      name: fullLesson.name,
      useCases: fullLesson.useCases
    }), /*#__PURE__*/_jsx(HasParentPlaylist, {
      fallback: /*#__PURE__*/_jsx(ACNavigatorButton, {
        active: contentId === TRACK_RECOMMENDATIONS_URL_FRAGMENT,
        helpText: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "learningCenter.tracks.recommendations.navigation.subheading"
        }),
        title: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "learningCenter.tracks.recommendations.navigation.heading"
        }),
        to: `/lessons/${lessonId}/${TRACK_RECOMMENDATIONS_URL_FRAGMENT}`
      }),
      children: /*#__PURE__*/_jsx(NextPlaylistContent, {
        lessonId: Number(lessonId),
        render: ({
          title,
          subtitle,
          url,
          urlFragments
        }) => /*#__PURE__*/_jsx(ACNavigatorButton, {
          active: urlFragments.includes(contentId),
          helpText: subtitle,
          title: title,
          to: url
        })
      })
    })]
  });
  return /*#__PURE__*/_jsx(LearningContentInnerWrapper, {
    children: /*#__PURE__*/_jsxs("div", {
      children: [/*#__PURE__*/_jsx(View, {
        insetBlockStart: "0px",
        position: "sticky",
        zIndex: 2,
        children: !isLoading && /*#__PURE__*/_jsx(LearningPageHeader, {
          ContentName: contentType === 'quiz' ? /*#__PURE__*/_jsx(FormattedMessage, {
            message: "learningCenter.lessonNavigator.quiz"
          }) : contentName,
          additionalTrackingData: lessonTrackingData,
          availableLanguages: fullLesson.availableLanguages,
          defaultBackLink: {
            pathname: '/',
            search: ''
          },
          language: language,
          languageLinkedContent: fullLesson.languageLinkedContent,
          lessonId: lessonId
        })
      }), isLoading ? /*#__PURE__*/_jsx(LoadingSpinner, {
        grow: true,
        minHeight: 200
      }) : /*#__PURE__*/_jsx(NavMarker, {
        name: "LESSON_VIEW_LOADED",
        children: /*#__PURE__*/_jsxs(Flex, {
          borderBlockEndStyle: !isMobile ? 'solid' : undefined,
          borderColor: "border-core-subtle",
          borderEndEndRadius: "400",
          borderEndStartRadius: "400",
          borderInlineStyle: !isMobile ? 'solid' : undefined,
          borderWidth: !isMobile ? '100' : undefined,
          flexDirection: isMobile ? 'column-reverse' : 'row',
          overflow: "clip",
          children: [!isMobile ? /*#__PURE__*/_jsx(LearningPageSidebar, {
            id: "sidebarNavigator",
            withSubNav: 1,
            children: navigator
          }) : /*#__PURE__*/_jsxs(View, {
            inlineSize: "100%",
            children: [/*#__PURE__*/_jsx(HR, {
              distance: "flush"
            }), navigator]
          }), /*#__PURE__*/_jsx(LearningPageMainSection, {
            minInlineSize: "0",
            children: /*#__PURE__*/_jsx(HasParentPlaylist, {
              fallback: lesson.get('inactiveRedirectUrl') || lessonRedirectUrl ? /*#__PURE__*/_jsx(Redirect, {
                to: {
                  pathname: lesson.get('inactiveRedirectUrl') || lessonRedirectUrl,
                  search: window.location.search
                }
              }) : /*#__PURE__*/_jsx(LessonPage, {
                contentId: contentId,
                isRequesting: lesson.get('isRequesting'),
                lesson: fullLesson,
                onAdvanceContent: handleAdvanceContent,
                onResourceClick: trackResourceClick
              }),
              children: /*#__PURE__*/_jsx(NextPlaylistContent, {
                lessonId: lesson.get('id'),
                redirectUrl: lessonRedirectUrl || undefined,
                render: ({
                  navigate
                }) => !isLoading && lessonRedirectUrl ? /*#__PURE__*/_jsx(Redirect, {
                  to: Object.assign({}, location, {
                    pathname: lessonRedirectUrl
                  })
                }) : /*#__PURE__*/_jsx(LessonPage, {
                  contentId: contentId,
                  isRequesting: lesson.get('isRequesting'),
                  lesson: fullLesson,
                  onAdvanceContent: nextContentId ? handleAdvanceContent : navigate,
                  onResourceClick: trackResourceClick
                })
              })
            })
          })]
        })
      })]
    })
  });
};
export default Lesson;